import { Logout } from '@mui/icons-material';
import { Divider, Drawer, IconButton, Stack } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import T from 'src/basic-components/text';
import useAuth from 'src/hooks/useAuth';
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';
import { NAVBAR } from '../../../config';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import useResponsive from '../../../hooks/useResponsive';
import cssStyles from '../../../utils/cssStyles';
import CollapseButton from './CollapseButton';
import navConfig from './NavConfig';
import NavbarAccount from './NavbarAccount';
import useSettings from 'src/hooks/useSettings';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();
  const { themeMode } = useSettings();
  const { tiles, logout } = useAuth();

  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack direction="column" justifyContent="start" sx={{ height: '100%' }}>
        <Stack
          spacing={3}
          sx={{
            pt: 2,
            pb: 2,
            // px: 2.5,
            flexShrink: 0,
            ...(isCollapse && { alignItems: 'center' }),
          }}
        >
          <Stack flexDirection="column" justifyContent="end" flexGrow={1}>
            <Stack
              direction="row"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                paddingLeft: isCollapse ? 0 : 2,
              }}
              paddingLeft={2}
            >
              <Stack direction="row" flexDirection="row" alignItems="center" justifyContent="start">
                <Logo sx={{ width: 60, height: 60 }} />
                <Stack direction="row" flexDirection="row" alignItems="center" justifyContent="center">
                  {!isCollapse && (
                    <T variant="subtitle2" sx={{ fontSize: 13, width: '80%', textAlign: 'center' }}>
                      app-name
                    </T>
                  )}
                </Stack>
              </Stack>
              {isDesktop && !isCollapse && (
                <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
              )}
            </Stack>
          </Stack>
        </Stack>
        {!isCollapse && <Divider style={{ marginBottom: '1rem' }} />}
        <NavSectionVertical navConfig={navConfig(tiles || [])} isCollapse={isCollapse} />
        <Stack direction="column" justifyContent="end" sx={{ height: '100%' }}>
          {!isCollapse && <Divider />}
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 2 }}>
            <NavbarAccount isCollapse={isCollapse} />

            <IconButton
              onClick={logout}
              sx={{
                width: 40,
                height: 40,
              }}
            >
              <Logout
                sx={{
                  color: themeMode == 'dark' ? theme.palette.primary.contrastText : theme.palette.primary.dark,
                  mx: 2,
                }}
              />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
